import { type Stripe } from "stripe";

import { type Database } from "@fitness-app/supabase";

import {
  type CrossSellProductInfo,
  type Currency,
  type InvoicesClient,
  type OrderPaymentType,
  type ProductPrice,
  type ProductSubscriptionInterval,
  type RecurringInterval,
} from "./Product";
import { type FunnelStepType, type SalesFunnelStep } from "./SalesFunnel";

export type OrderStatus = Database["public"]["Enums"]["OrderStatus"];

export type SessionMetadataEntity = Database["public"]["Tables"]["session_metadata"]["Row"];
export type OrderEntity = Database["public"]["Tables"]["product_order"]["Row"];

export interface FunnelState {
  currentStepId: string;
  currentStepPath: string;
  currentStepType: FunnelStepType;
  steps: (Pick<SalesFunnelStep, "id" | "path" | "type"> & {
    productId: string | null;
    state: "init" | "completed" | "rejected" | "processing" | "viewed";
  })[];
}

export type SessionMetadata = Omit<SessionMetadataEntity, "metadata" | "renewal" | "funnelState"> & {
  metadata?: Record<string, string | number | null | boolean> & {
    isMetaConversionAvailable?: boolean | null;
    fbp?: string | null;
    fbc?: string | null;
    utm_source?: string | null;
  };
  renewal?: boolean | null;
  funnelState?: null | FunnelState;
};

export const OrderStatusOption = {
  Created: "CREATED",
  Paid: "PAID",
  Finalized: "FREE_FINALIZED",
  Failed: "FAILED",
  Imported: "IMPORTED",
  Refunded: "REFUNDED",
  Scheduled: "SCHEDULED",
  /**
   * Zamówienie zamknięte świadomie, bez odmowy płatności (TRE-1999): porzucony
   * koszyk wygaszony przez task `/update-order`, rollback zamówienia, które
   * nigdy nie dotarło do bramki, anulowanie z panelu.
   *
   * Nie dotyczy rollbacków będących SKUTKIEM odmowy — odrzucony kod BLIK cofa
   * subskrypcję i alias, ale zamówienie zostaje `Failed`.
   *
   * `Failed` znaczy odtąd tylko jedno: bramka odrzuciła płatność. Przed tym
   * rozdzieleniem oba przypadki lądowały pod `Failed` i statystyki odmów kart
   * liczyły porzucone koszyki jako nieudane płatności.
   */
  Canceled: "CANCELED",
} as const;

/**
 * Dlaczego zamówienie zostało anulowane — trafia do `metadata.canceledReason`.
 * Sam status mówi „nie doszło do skutku, ale nie przez bank"; bez powodu nie da
 * się po fakcie odróżnić porzuconego koszyka od anulowania ręcznego, a to
 * pierwsze pytanie przy każdej analizie takiego zamówienia.
 */
export const OrderCancellationReason = {
  /** Task `/update-order` zastał zamówienie nieopłacone po oknie porzucenia. */
  AbandonedCheckout: "abandoned_checkout",
  /** Zamówienie powstało, ale transakcja w bramce nigdy nie ruszyła. */
  CheckoutRollback: "checkout_rollback",
  /** Decyzja z panelu — faktura uznana za nieściągalną, subskrypcja anulowana. */
  CanceledInPanel: "canceled_in_panel",
  /**
   * Faktura zvoidowana po stronie bramki (dashboard Stripe, `neutralizeLeftoverInvoices`)
   * — webhook `invoice.voided` (TRE-2167). Dług nie istnieje, więc zamówienie nie może
   * dalej wisieć jako `Failed` i zawyżać listy zaległości.
   */
  VoidedInGateway: "voided_in_gateway",
} as const;

export type OrderCancellationReason = (typeof OrderCancellationReason)[keyof typeof OrderCancellationReason];

/**
 * Decyzja o dokumencie fiskalnym podjęta przy potwierdzaniu płatności manualnej
 * (TRE-2006) — trafia do `metadata.invoiceIssuance` na zamówieniu, bo tylko ten
 * nośnik pokrywa obie ścieżki potwierdzenia: bezpośrednią publikację eventu
 * i ścieżkę rat, gdzie event wychodzi dopiero z webhooka `invoice.paid`.
 * Invoices-service czyta dyrektywę po załadowaniu zamówienia.
 *
 * `mode: "skip"` — dokumentu nie wystawiamy automatycznie; księgowość wystawi
 * go ręcznie („Wystaw paragon / Wystaw fakturę"), gdy przelew trafi na konto.
 * `typeOverride` — wymuszenie typu dokumentu dla osoby prywatnej; firma zawsze
 * dostaje fakturę VAT i nadpisanie jej nie dotyczy.
 */
export type OrderInvoiceIssuanceDirective = {
  mode: "auto" | "skip";
  typeOverride?: "receipt" | "invoice" | null;
  requestedBy?: string | null;
  requestedAt?: string | null;
};

/** Bezpieczny odczyt dyrektywy z luźnego worka `metadata` — złe kształty ignorujemy. */
export const readInvoiceIssuanceDirective = (
  metadata: Record<string, unknown> | null | undefined,
): OrderInvoiceIssuanceDirective | null => {
  const candidate = metadata?.invoiceIssuance;

  if (typeof candidate !== "object" || candidate === null) {
    return null;
  }

  const { mode, typeOverride } = candidate as { mode?: unknown; typeOverride?: unknown };

  if (mode !== "auto" && mode !== "skip") {
    return null;
  }

  return {
    mode,
    typeOverride: typeOverride === "receipt" || typeOverride === "invoice" ? typeOverride : null,
  };
};

/**
 * Statusy, z których zamówienie z płatnością ręczną wolno potwierdzić jako
 * opłacone — czyli te, z których `OrderStatusMachine` dopuszcza przejście do
 * `Paid`.
 *
 * Mieszka tu, a nie w services-shared, bo pytanie zadaje panel (warunek
 * widoczności akcji), a maszyna stanów jest backendowa i panel nie może jej
 * zaimportować. Parzystości obu list pilnuje test przy samej maszynie
 * (`OrderStatusMachine.test.ts`) — bez tego warunek w UI rozjeżdżał się z nią
 * po kolei: najpierw brakło `Failed` (rata ubita przez crona, opłacona później
 * przelewem — TRE-1846), potem `Scheduled` (przedpłata za ratę przed terminem).
 *
 * `ConfirmOrderWithManualPaymentUseCase` nie bramkuje statusu — loguje tylko
 * nieregularne przejście — więc to UI decyduje, czy akcja ma sens.
 */
export const MANUAL_ORDER_CONFIRMABLE_STATUSES: readonly OrderStatus[] = [
  OrderStatusOption.Created,
  OrderStatusOption.Scheduled,
  OrderStatusOption.Failed,
  // Anulowane też — z tego samego powodu co `Failed`: pieniądze potrafią
  // przyjść po fakcie. Porzucony koszyk wygaszony po 30 minutach bywa
  // opłacony przelewem następnego dnia, a wtedy panel musi umieć to
  // potwierdzić (TRE-1846 dla `Failed`, TRE-1999 dla `Canceled`).
  //
  // ZASTRZEŻENIE: zamówienie ręczne anulowane z panelu, które miało OTWARTĄ
  // fakturę Stripe'a, wychodzi z niej jako `uncollectible`, a
  // `ConfirmOrderWithManualPaymentUseCase` przyjmuje tylko `open` i `draft` —
  // takie potwierdzenie odbije się czystym `GatewayInvoiceNotPayable`. Dla
  // zamówień bez faktury i z fakturą w `draft` ścieżka działa. Rozszerzenie
  // bramki potwierdzania o `uncollectible` to osobna decyzja (TRE-1999).
  OrderStatusOption.Canceled,
];

export const canConfirmManualOrder = (status: string | null | undefined): boolean =>
  MANUAL_ORDER_CONFIRMABLE_STATUSES.includes(status as OrderStatus);

/**
 * Czy na racie BLIK recurring wisi obciążenie aliasu, o którym nie wiemy jeszcze,
 * jak się skończyło. Watchdog zapisuje w `blikWatchdogHandledTx` transakcję,
 * którą rozstrzygnął; dopóki `blikTransactionId` wskazuje inną, TPay może ją
 * jeszcze rozliczyć. Potwierdzenie ręczne w tym oknie = rata pobrana dwa razy.
 */
export const hasBlikChargeInFlight = (metadata: Record<string, unknown> | null | undefined): boolean => {
  const transactionId = metadata?.blikTransactionId;
  return typeof transactionId === "string" && metadata?.blikWatchdogHandledTx !== transactionId;
};

/**
 * Statusy, z których panel wolno domknąć zamówienie jako nieopłacone (`Failed`)
 * i jako anulowane (`Canceled`) — czyli te, z których `OrderStatusMachine`
 * dopuszcza dane przejście.
 *
 * Mieszkają tu z tego samego powodu co `MANUAL_ORDER_CONFIRMABLE_STATUSES`:
 * pytanie zadaje panel, a maszyna jest backendowa i panel jej nie zaimportuje.
 * Parzystości pilnuje test przy maszynie — bez niego menu prędzej czy później
 * zaczyna oferować akcję, którą serwis odrzuci.
 *
 * Asymetria jest celowa: odmowę da się potem anulować (uznajemy martwą ratę za
 * zamkniętą), ale anulowania nie da się zamienić w odmowę — to była nasza
 * decyzja, bank nie miał czego odrzucić (TRE-1999).
 */
export const ORDER_CLOSABLE_AS_FAILED_STATUSES: readonly OrderStatus[] = [
  OrderStatusOption.Created,
  OrderStatusOption.Scheduled,
];

export const ORDER_CLOSABLE_AS_CANCELED_STATUSES: readonly OrderStatus[] = [
  OrderStatusOption.Created,
  OrderStatusOption.Scheduled,
  OrderStatusOption.Failed,
];

export const canCloseOrderAsFailed = (status: string | null | undefined): boolean =>
  ORDER_CLOSABLE_AS_FAILED_STATUSES.includes(status as OrderStatus);

export const canCloseOrderAsCanceled = (status: string | null | undefined): boolean =>
  ORDER_CLOSABLE_AS_CANCELED_STATUSES.includes(status as OrderStatus);

/**
 * Whether the order's payment is settled — the question the success page asks
 * before it congratulates anyone or reports a conversion.
 *
 * `Created` is the one that matters: the customer was handed to a gateway and has
 * not come back paid. TPay sends both "payment done" and "back to shop" to the same
 * payer URL, so an abandoned checkout reaches the success page exactly like a
 * completed one — the order status is all that tells them apart. It is routine with
 * installments, where the bank's decision can be days away.
 */
export const isOrderPaymentConfirmed = (status: string | null | undefined): boolean =>
  (
    [
      OrderStatusOption.Paid,
      OrderStatusOption.Finalized,
      OrderStatusOption.Imported,
      OrderStatusOption.Scheduled,
    ] as readonly string[]
  ).includes(status ?? "");

/**
 * When the money arrived, or null.
 *
 * `metadata.paidAt` is stamped at settlement (TRE-2136), so it exists only on
 * orders paid after that shipped. Older ones yield null rather than an
 * approximation: `updatedAt` would look like an answer on all of them while
 * being the last time anything touched the row — a refund, a metadata patch —
 * and a date people reconcile payments against cannot be a guess. A refunded
 * order keeps its date: the payment did happen.
 */
export const getOrderPaidAt = (order: {
  status: string | null;
  metadata?: Record<string, unknown> | null;
}): string | null => {
  const paidAt = typeof order.metadata?.paidAt === "string" ? order.metadata.paidAt : null;
  if (!paidAt || (order.status !== OrderStatusOption.Paid && order.status !== OrderStatusOption.Refunded)) {
    return null;
  }
  return paidAt;
};

export interface ClientInvoiceData {
  firstName: string;
  lastName: string;
  companyName: string;
  nip: string;
  street: string;
  postalCode: string;
  city: string;
  country?: string;
  isLegalEntity?: boolean;
}

/**
 * Whether the buyer is a business rather than a private individual. Drives both the
 * `buyer_company` flag (which decides KSeF auto-send eligibility) and the choice between
 * a plain VAT invoice and a receipt with an invoice issued to it.
 */
export const isCompanyBuyer = (client: ClientInvoiceData | undefined | null): boolean => {
  // A tax id or company name identifies a business on its own — an explicit
  // `isLegalEntity: false` must not hide it, or such a buyer would be invoiced as a
  // private individual and reported to KSeF as `buyer_company: false`.
  return Boolean(client?.isLegalEntity || client?.companyName?.trim() || client?.nip?.trim());
};

export interface InsufficientFunds {
  status: "insufficient_funds";
}

export interface ConfirmTransaction {
  status: "confirm_transaction";
  url: string;
}

export type RequiresAction = InsufficientFunds | ConfirmTransaction;

export interface SuccessRefund {
  timestamp: number;
  amount: number;
  currency: Currency;
  creatorId: string;
  refundId: string | "manual";
}

export interface FailedRefund {
  creatorId: string;
  errorMessage: string;
  timestamp: number;
}

export type Refund = SuccessRefund | FailedRefund;
export type InvoiceKsefStatus =
  | "ok"
  | "processing"
  | "send_error"
  | "server_error"
  | "not_applicable"
  | "not_connected"
  | "demo_ok"
  | "demo_processing"
  | "demo_send_error"
  | "demo_server_error"
  | "demo_not_applicable"
  | "demo_not_connected"
  | null;

/** KSeF statuses after which Fakturownia will not deliver the invoice without manual intervention. */
export const TERMINAL_KSEF_STATUSES: ReadonlySet<NonNullable<InvoiceKsefStatus>> = new Set([
  "send_error",
  "server_error",
  "not_connected",
  "demo_send_error",
  "demo_server_error",
  "demo_not_connected",
] as const);

export const isTerminalKsefStatus = (status: InvoiceKsefStatus | undefined): boolean =>
  Boolean(status && TERMINAL_KSEF_STATUSES.has(status));

/**
 * KSeF statuses that can still change on their own. An invoice sits here while KSeF
 * processes it, so its stored metadata (notably the KSeF number) has to be re-read
 * until it settles into a ready or terminal status.
 */
export const IN_FLIGHT_KSEF_STATUSES: ReadonlySet<NonNullable<InvoiceKsefStatus>> = new Set([
  "processing",
  "demo_processing",
] as const);

export const isInFlightKsefStatus = (status: InvoiceKsefStatus | undefined): boolean =>
  Boolean(status && IN_FLIGHT_KSEF_STATUSES.has(status));

export interface InvoiceKsefMetadata {
  status: InvoiceKsefStatus;
  govId?: string | null;
  sentAt?: string | null;
  sellDate?: string | null;
  errors?: string[] | null;
  verificationLink?: string | null;
  link?: string | null;
  correctedInvoiceNumber?: string | null;
}

export type InvoiceEmailDeliveryStatus = "sent" | "pending_ksef" | "failed";

export interface InvoiceEmailDelivery {
  status: InvoiceEmailDeliveryStatus;
  attemptedAt: string;
  message?: string | null;
  details?: string | null;
}

export interface ConnectedIssuedInvoice {
  id: string | number;
  number: string | number;
  client: InvoicesClient;
  accountUrl: string;
  accountName: string;
  isReceipt?: boolean;
  ksef?: InvoiceKsefMetadata;
  emailDelivery?: InvoiceEmailDelivery;
  /** Receipt this invoice was issued to (faktura do paragonu). */
  receipt?: { id: string | number; number: string | number };
  /** Correction issued for this document after a refund (faktura korygująca). */
  correction?: {
    id: string | number;
    number: string | number;
    issuedAt: string;
  };
}

export type PaidLastOrder = {
  amount: number;
  amountForMainProduct: number;
  currency: Currency;
  priceId: string;
  orderId: string;
  free: false;
  paymentGatewayInvoiceId?: string | null;
  customInvoiceName?: string;
  priceDescription: string | null;
  invoiceMetadata?: Record<string, string | number>;
  priceAccessPeriod?: ProductPrice["accessPeriod"] | null;
  type?: ProductPrice["type"];
};

export type FreeLastOrder = {
  type?: never;
  priceAccessPeriod?: never;
  orderId: string;
  free: true;
  priceId: null;
  amount?: 0;
  current?: never;
  currency?: never;
  paidPhase?: never;
  amountForMainProduct?: never;
  invoiceMetadata?: Record<string, string | number>;
};

export type LastOrder = PaidLastOrder | FreeLastOrder;

export type OrderItem = LastOrder & {
  productName: string;
  installments?: number | null;
  paidPhase?: Stripe.SubscriptionSchedule.Phase | null;
  accessPeriod?: {
    startAt: number;
    endAt: number;
  } | null;
};

/**
 * Coupon snapshot attached to an order item / order metadata. Mirrors the zod schema
 * in `CreateOrderDTOValidator.appliedCoupon`. Two id-shapes coexist:
 *  - `promo_…` — a synced Stripe promotion code (Stripe owns redemption bookkeeping)
 *  - a uuid — our internal admin-managed discount (tracked by DiscountService)
 * `DiscountService.isOurDiscountId` discriminates between them.
 */
/**
 * Hard cap on stacked discount codes per order. Shared by the checkout UI (stops
 * offering "add another code") and the order DTO validators (rejects abusive
 * payloads) so the two can't drift apart.
 */
export const MAX_APPLIED_COUPONS = 5;

export interface AppliedCoupon {
  id: string;
  code: string;
  active: boolean;
  coupon: {
    id: string;
    /** Nullable to fit `Stripe.PromotionCode.coupon.name`; our internal coupons always set it. */
    name: string | null;
    amount_off: number | null;
    percent_off: number | null;
    /**
     * Stripe coupon duration. Decides whether the code stops at the first
     * payment ("once") or keeps discounting the following cycles
     * ("forever"/"repeating"). Absent on orders created before it was recorded.
     */
    duration?: "once" | "repeating" | "forever" | null;
    applies_to?: {
      products: string[];
    } | null;
  };
  object?: string | null;
  livemode?: boolean | null;
  max_redemptions?: number | null;
  expires_at?: number | null;
}

export type OrderListItem = {
  productName: string | null;
  productId: string;
  priceAmount: number | null;
  priceCurrency: Currency | null;
  priceId: string;
  priceDescription: string | null;
  priceAccessPeriod?: ProductPrice["accessPeriod"] | null;
  type?: ProductPrice["type"];
  crossSell?: boolean;
  installments?: number | null;
  customDuration?: {
    count: number;
    unit: RecurringInterval;
  } | null;
  discount?: AppliedCoupon | null;
  /**
   * All stacked discount codes applied to this line item (Stripe/card only).
   * `discount` stays the primary (first) code for backward compatibility;
   * `discounts` is the full list the redemption ledger iterates over. Absent /
   * single-element on legacy single-code orders.
   */
  discounts?: AppliedCoupon[] | null;
  customInvoiceName?: string | null;
};

export interface OrderRevenueBreakdown {
  /** Kwota faktycznie pobrana przy zakupie — pierwsza płatność po rabatach. */
  paid: number;
  /** Wartość całego kontraktu po rabatach: pierwsza płatność + pozostałe raty. */
  projected: number;
  installments: number;
}

/** Minimalny kształt zamówienia, z którego da się policzyć przychód. */
type RevenueSource = {
  price?: { amount?: number | null; installments?: number | null } | null;
  items?: Pick<OrderListItem, "priceAmount" | "installments" | "discount" | "discounts">[] | null;
  metadata?: Record<string, unknown> | null;
};

const parseInstallments = (value: unknown): number | null => {
  const parsed = Number(value);
  // Ułamek przeszedłby przez `isFinite`, a pętla cykli i tak zaokrągliłaby go
  // w górę — 2.5 raty policzyłoby się jak 3 płatności.
  return Number.isInteger(parsed) && parsed > 1 ? parsed : null;
};

const appliesToCycle = (coupon: AppliedCoupon["coupon"], cycleIndex: number): boolean => {
  if (cycleIndex === 0) {
    return true;
  }
  // `repeating` niesie w Stripe `duration_in_months`, którego nie zapisujemy w snapshocie
  // zamówienia — traktujemy je jak `forever`, czyli zaniżamy projekcję zamiast ją zawyżać.
  return coupon.duration === "forever" || coupon.duration === "repeating";
};

/** Kwota jednej raty po nałożeniu rabatów obowiązujących w danym cyklu. */
const getInstallmentAmount = (unitAmount: number, coupons: AppliedCoupon[], cycleIndex: number): number =>
  coupons.reduce((amount, { coupon }) => {
    if (amount <= 0 || !appliesToCycle(coupon, cycleIndex)) {
      return amount;
    }

    if (coupon.percent_off) {
      return Math.max(0, amount - (amount * coupon.percent_off) / 100);
    }

    return coupon.amount_off ? Math.max(0, amount - coupon.amount_off) : amount;
  }, unitAmount);

/**
 * Rozbija zamówienie na kwotę faktycznie pobraną i wartość całego kontraktu.
 *
 * `order.price.amount` to wyłącznie pierwsza płatność po rabatach, więc mnożenie jej
 * przez liczbę rat zeruje całe zamówienie, gdy pierwsza rata poszła z kodem 100%.
 * Projekcja wychodzi więc od pełnej raty (`items[0].priceAmount`) i nakłada na kolejne
 * cykle tylko te rabaty, które je obejmują (`coupon.duration`). Pierwszy cykl bierzemy
 * wprost z `price.amount` — to prawda pochodząca ze Stripe, nie nasze przeliczenie.
 */
export const getOrderRevenueBreakdown = (order: RevenueSource): OrderRevenueBreakdown => {
  const paid = typeof order.price?.amount === "number" ? order.price.amount : 0;
  const item = order.items?.[0];

  const installments =
    parseInstallments(order.metadata?.installments) ??
    parseInstallments(item?.installments) ??
    parseInstallments(order.price?.installments) ??
    1;

  if (installments <= 1) {
    return { paid, projected: paid, installments: 1 };
  }

  const unitAmount = item?.priceAmount ?? 0;

  if (!unitAmount) {
    // Brak snapshotu pełnej raty (zamówienia sprzed `items`) — zostaje stare przybliżenie.
    return { paid, projected: paid * installments, installments };
  }

  const coupons = item?.discounts ?? (item?.discount ? [item.discount] : []);

  let projected = paid;
  for (let cycleIndex = 1; cycleIndex < installments; cycleIndex++) {
    projected += getInstallmentAmount(unitAmount, coupons, cycleIndex);
  }

  return { paid, projected, installments };
};

export interface OrderDiscountBreakdown {
  /** Codes applied to this order, deduplicated, in the order they were found. */
  codes: string[];
  /** Catalogue price of everything on the order, in cents — what it would have cost with no code. */
  listAmount: number;
  /** What was actually charged for the cycle this order represents, in cents. */
  paid: number;
  /** `listAmount - paid`, floored at zero. */
  discountAmount: number;
  hasDiscount: boolean;
  /** Charged nothing — a 100% code, a free extension, or a manual comp. */
  isFree: boolean;
}

/** Minimal order shape a discount can be read from. */
type DiscountSource = {
  price?: { amount?: number | null } | null;
  items?: Pick<OrderListItem, "priceAmount" | "discount" | "discounts">[] | null;
  metadata?: Record<string, unknown> | null;
};

const readCoupons = (value: unknown): AppliedCoupon[] => {
  if (!value) {
    return [];
  }

  const candidates = Array.isArray(value) ? value : [value];

  return candidates.filter(
    (candidate): candidate is AppliedCoupon =>
      typeof candidate === "object" && candidate !== null && typeof (candidate as AppliedCoupon).code === "string",
  );
};

/**
 * What a code took off this order.
 *
 * Analytics that count paid orders read a 100% redemption as a full-price sale unless they
 * subtract this: `price.amount` is already the discounted number, so revenue stays honest
 * while the transaction count silently treats a giveaway as a purchase.
 *
 * The catalogue price comes from `items[].priceAmount` — every line, cross-sell included,
 * because `price.amount` covers them too. On installment orders both sides describe the SAME
 * cycle (the item snapshot holds one full installment), so the difference is the discount on
 * the paid cycle rather than on the whole plan. Codes are read from the line items and from
 * `metadata.appliedCoupon`; the order-level `discounted` / `discounts` columns are write-only
 * legacy mirrors and are deliberately ignored.
 */
export const getOrderDiscountBreakdown = (order: DiscountSource): OrderDiscountBreakdown => {
  const paid = typeof order.price?.amount === "number" ? order.price.amount : 0;
  const items = order.items ?? [];

  const codes: string[] = [];
  const addCode = (coupon: AppliedCoupon) => {
    if (!codes.includes(coupon.code)) {
      codes.push(coupon.code);
    }
  };

  for (const item of items) {
    for (const coupon of item.discounts ?? readCoupons(item.discount)) {
      addCode(coupon);
    }
  }

  for (const coupon of readCoupons(order.metadata?.appliedCoupon)) {
    addCode(coupon);
  }

  // Without the item snapshot there is no catalogue price to compare against, so the order
  // reports no discount rather than a made-up one.
  const listAmount = items.length ? items.reduce((total, item) => total + (item.priceAmount ?? 0), 0) : paid;

  const discountAmount = Math.max(0, listAmount - paid);

  return {
    codes,
    listAmount,
    paid,
    discountAmount,
    hasDiscount: codes.length > 0 || discountAmount > 0,
    isFree: paid === 0,
  };
};

export interface WarmupPeriod {
  prices: string[];
  email: string;
  amount: number;
  startDate: number;
  endDate: number;
}

export type BaseOrder = Omit<
  OrderEntity,
  | "client"
  | "connectedInvoice"
  | "discounts"
  | "metadata"
  | "refund"
  | "paymentType"
  | "price"
  | "requiresAction"
  | "previousSubscriptionPrice"
  | "orderNumber"
  | "crossSell"
  | "items"
> & {
  paymentType: OrderPaymentType;
  ownerEmail: string;
  orderNumber: number;
  price: OrderItem | null;
  client: ClientInvoiceData;
  session: string | null;
  connectedInvoice?:
    | null
    | ConnectedIssuedInvoice
    | { errorMessage: string; timestamp: number; errorData?: null | { message?: string; details?: string } | string };
  requiresAction?: RequiresAction;
  metadata?: Record<string, string | number | null | boolean | any> & {
    sessionMetadata?: SessionMetadata;
    warmupPeriod?: WarmupPeriod;
    /** Ustawiane razem ze statusem `Canceled` (TRE-1999). */
    canceledReason?: OrderCancellationReason;
    /** ISO — kiedy bramka zvoidowała fakturę tego zamówienia (webhook `invoice.voided`, TRE-2167). */
    voidedAt?: string;
    /** ISO — kiedy faktura została oznaczona jako nieściągalna w bramce (`invoice.marked_uncollectible`). */
    uncollectibleAt?: string;
    /** Ustawiane przy potwierdzaniu płatności manualnej (TRE-2006). */
    invoiceIssuance?: OrderInvoiceIssuanceDirective;
  };
  /**
   * @deprecated Write-only. Stripe flow keeps it updated, TPay leaves it `false`, and no consumer
   * reads it. Source of truth for "did this order use a code" is `metadata.appliedCoupon` (set at
   * order creation by both gateways) and `items[].discount` (per line item). For redemption history
   * use the `discount_redemption` ledger table.
   */
  discounted: boolean;
  /**
   * @deprecated Write-only jsonb mirror of the Stripe.Discount object. Not read anywhere
   * (`ActiveCampaignMailingService.orderDiscounts` is a separately-named runtime payload, not
   * sourced from this column). Use `metadata.appliedCoupon`, `items[].discount`, and the
   * `discount_redemption` ledger instead.
   */
  discounts?: Stripe.LineItem.Discount[] | Stripe.Discount[] | null;
  refund: Refund | null;
  paymentGatewayInvoiceId?: string | null;
  crossSell?: CrossSellProductInfo[];
  items?: OrderListItem[];
};

export interface FreeOrder extends BaseOrder {
  price: null;
  priceId: null;
}
export interface PaidOrder extends BaseOrder {
  price: OrderItem;
  priceId: string;
}

export interface OrderWithProration extends BaseOrder {
  priceId: string;
  previousSubscriptionPrice: {
    amount: number;
    currency: Currency;
    id: string;
    interval_count: number;
    interval: ProductSubscriptionInterval;
  };
  price: {
    amountForMainProduct: number;
    priceAccessPeriod?: ProductPrice["accessPeriod"] | null;
    type?: ProductPrice["type"];
    productName: string;
    priceDescription: string | null;
    accessPeriod?: {
      startAt: number;
      endAt: number;
    } | null;
    amount: number;
    currency: Currency;
    priceId: string;
    orderId: string;
    free: false;
    paymentGatewayInvoiceId?: string | null;
    customInvoiceName?: string;
    paidPhase?: never;
    installments?: number | null;
  } & {
    baseAmount: number;
    prorated: boolean;
    interval_count: number;
    interval: ProductSubscriptionInterval;
  };
}

export type Order = BaseOrder | PaidOrder | OrderWithProration | FreeOrder;
